import { z } from "zod";

/**
 * Shared DTO cho Admin Group Lifecycle — suspend / restore nhóm (US-AE4.F3-0016 / @UC9032).
 * Một nguồn sự thật cho api (S01 — endpoint suspend/restore · S02 — freeze code) và admin web
 * (S03 — dialog lý do + action), tránh drift shape ở 2 nơi (DRY). Mirror admin-user-lifecycle.
 *
 * BẢO MẬT (invariants):
 * - Request `.strict()` — chỉ nhận `reason`, chặn field lạ (chống lẫn PII/secret vào body).
 * - Response chỉ định danh + status — KHÔNG PII/secret/tiền.
 * - Lý do (reason) lưu vào audit_log ở backend (KHÔNG cột riêng, human-decision F3 §6 default).
 */

/** Độ dài tối đa lý do đình chỉ — chặn input phình; đủ cho ghi chú nghiệp vụ. */
export const SUSPEND_GROUP_REASON_MAX_LENGTH = 500;

/** Body POST /admin/groups/:id/suspend — reason bắt buộc (1..500). `.strict()` chặn field lạ. */
export const suspendGroupRequestSchema = z
  .object({
    reason: z.string().min(1).max(SUSPEND_GROUP_REASON_MAX_LENGTH),
  })
  .strict();

export type SuspendGroupRequest = z.infer<typeof suspendGroupRequestSchema>;

/**
 * US-AE16.F7-0434-S02 (`@UC9032`) — body của `POST /v1/admin/groups/:id/undelete`.
 *
 * `reason` BẮT BUỘC, mirror `suspend`: khôi phục dữ liệu của người khác là hành động cần giải trình.
 * Dùng LẠI cùng giới hạn độ dài — không đẻ hằng thứ hai cho cùng một loại ô nhập.
 */
export const undeleteGroupRequestSchema = z
  .object({
    reason: z.string().min(1).max(SUSPEND_GROUP_REASON_MAX_LENGTH),
  })
  .strict();

export type UndeleteGroupRequest = z.infer<typeof undeleteGroupRequestSchema>;

/** Response suspend: status='suspended'. */
export const suspendGroupResponseSchema = z.object({
  groupId: z.string(),
  status: z.literal("suspended"),
});

export type SuspendGroupResponse = z.infer<typeof suspendGroupResponseSchema>;

/** Response restore: status='active'. */
export const restoreGroupResponseSchema = z.object({
  groupId: z.string(),
  status: z.literal("active"),
});

export type RestoreGroupResponse = z.infer<typeof restoreGroupResponseSchema>;

/**
 * Code 409 khi member cố WRITE nghiệp vụ (dues/expense/payment/config) trên nhóm đã đình chỉ
 * (S02 FundAccessGuard). Dùng chung api ↔ UI để hiển thị lỗi nhất quán. READ (GET) KHÔNG bị chặn.
 */
export const GROUP_SUSPENDED_CODE = "GROUP_SUSPENDED" as const;
export const GROUP_SUSPENDED_MESSAGE =
  "Nhóm đang bị đình chỉ — không thể tạo thu/chi/thanh toán. Vẫn xem minh bạch được.";

/**
 * Code 409 khi suspend/restore nhóm gặp serialization failure (Postgres 40001 → Prisma P2034):
 * hai transaction SERIALIZABLE cùng đổi groups.status commit song song ⇒ 1 cái bị huỷ. Caller retry.
 */
export const GROUP_STATUS_CONCURRENT_UPDATE_CODE =
  "GROUP_STATUS_CONCURRENT_UPDATE" as const;
